import { useEffect, useMemo, useRef } from "react";
import {
  useCartState,
  useCartMethodExecute,
  type Cart,
} from "@made-people/grease-cart";
import {
  useGreaseGrowthbook,
  useGreaseGrowthbookFeatureValue,
} from "@made-people/grease-growthbook";

const FREE_SHIPPING_FEATURE_KEY = "free-shipping-4-tiers";

type FreeShippingCurrencyConfig = { threshold: string; voucherCode?: string };
type FreeShippingThresholdConfig = Record<
  string,
  FreeShippingCurrencyConfig | string
> & {
  voucherCode?: string;
};

function isFreeShippingCurrencyConfig(
  value: unknown,
): value is FreeShippingCurrencyConfig {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as FreeShippingCurrencyConfig).threshold === "string"
  );
}

export type GrowthbookFreeShippingCurrencyConfig = {
  threshold: string | null;
  currency: string;
  voucherCode: string;
};

export type MmFreeShippingThresholdData = {
  growthbookCurrencyConfig: GrowthbookFreeShippingCurrencyConfig | null;
  shouldUseGrowthbookThreshold: () => boolean;
  shouldAutoApplyVoucher: boolean;
  shouldAutoRemoveVoucher: boolean;
  growthbookVoucherCodesToRemove: string[];
};

export function useMmFreeShippingThresholdData(): MmFreeShippingThresholdData {
  const { cart } = useCartState();
  const freeShippingThresholdConfig =
    useGreaseGrowthbookFeatureValue<FreeShippingThresholdConfig | null>(
      FREE_SHIPPING_FEATURE_KEY,
      null,
    );

  const growthbookCurrencyConfig =
    useMemo((): GrowthbookFreeShippingCurrencyConfig | null => {
      const config = freeShippingThresholdConfig;
      if (!config || typeof config !== "object") return null;
      if (Object.keys(config).length === 0) {
        return { threshold: null, currency: "SEK", voucherCode: "NA" };
      }
      const resolvedCurrency = cart?.currencyCode;
      if (!resolvedCurrency || typeof resolvedCurrency !== "string")
        return null;

      const currencyKey = Object.keys(config).find(
        (k) => k.toUpperCase() === resolvedCurrency.toUpperCase(),
      );
      const currencyConfig = currencyKey ? config[currencyKey] : undefined;

      if (!isFreeShippingCurrencyConfig(currencyConfig)) return null;

      const voucherCode =
        (typeof currencyConfig.voucherCode === "string"
          ? currencyConfig.voucherCode
          : undefined) ??
        (typeof config.voucherCode === "string"
          ? config.voucherCode
          : undefined);

      if (!currencyConfig.threshold || !voucherCode) return null;

      return {
        threshold: String(currencyConfig.threshold),
        currency: resolvedCurrency,
        voucherCode: String(voucherCode),
      };
    }, [freeShippingThresholdConfig, cart?.currencyCode]);

  const cartTotal = useMemo(() => {
    const totals = cart?.totals;
    if (!totals?.grandTotal || totals.shipping === undefined) return 0;
    return (totals.grandTotal.number ?? 0) - (totals.shipping.number ?? 0);
  }, [cart?.totals]);

  const shouldUseGrowthbookThreshold = (): boolean =>
    Boolean(growthbookCurrencyConfig);

  const targetVoucherApplied = useMemo(() => {
    if (!growthbookCurrencyConfig || !cart?.vouchers) return false;

    return cart.vouchers.some(
      (v) =>
        v.type === "code" && v.code === growthbookCurrencyConfig.voucherCode,
    );
  }, [cart?.vouchers, growthbookCurrencyConfig]);

  const isExpressShipping = useMemo(
    () =>
      cart?.shippingMethod?.code?.toUpperCase().includes("EXPRESS") ?? false,
    [cart?.shippingMethod?.code],
  );

  const shouldAutoApplyVoucher = useMemo(() => {
    if (!growthbookCurrencyConfig?.threshold || isExpressShipping) return false;
    const threshold = parseFloat(growthbookCurrencyConfig.threshold);
    if (Number.isNaN(threshold) || cartTotal < threshold) return false;
    return !targetVoucherApplied;
  }, [
    growthbookCurrencyConfig,
    cartTotal,
    targetVoucherApplied,
    isExpressShipping,
  ]);

  const shouldAutoRemoveVoucher = useMemo(() => {
    if (isExpressShipping && targetVoucherApplied) return true;
    if (!growthbookCurrencyConfig?.threshold || !targetVoucherApplied)
      return false;
    const threshold = parseFloat(growthbookCurrencyConfig.threshold);
    if (Number.isNaN(threshold)) return false;
    return cartTotal < threshold;
  }, [
    growthbookCurrencyConfig,
    targetVoucherApplied,
    cartTotal,
    isExpressShipping,
  ]);

  const growthbookVoucherCodesToRemove = useMemo(() => {
    const targetCode = growthbookCurrencyConfig?.voucherCode ?? null;
    if (!cart?.vouchers) return [];
    return cart.vouchers
      .filter(
        (v) =>
          v.type === "code" &&
          v.code !== targetCode &&
          v.name.includes("Free Shipping A/B test"),
      )
      .map((v) => (v.type === "code" ? v.code : ""))
      .filter(Boolean);
  }, [cart?.vouchers, growthbookCurrencyConfig?.voucherCode]);

  return {
    growthbookCurrencyConfig,
    shouldUseGrowthbookThreshold,
    shouldAutoApplyVoucher,
    shouldAutoRemoveVoucher,
    growthbookVoucherCodesToRemove,
  };
}

export function useMmFreeShippingThreshold() {
  const data = useMmFreeShippingThresholdData();
  return {
    growthbookCurrencyConfig: data.growthbookCurrencyConfig,
    shouldUseGrowthbookThreshold: data.shouldUseGrowthbookThreshold,
  };
}

function getVoucherSyncActions(
  cart: Cart | undefined,
  config: FreeShippingThresholdConfig | null,
) {
  let growthbookCurrencyConfig: GrowthbookFreeShippingCurrencyConfig | null =
    null;
  if (config && typeof config === "object") {
    if (Object.keys(config).length === 0) {
      growthbookCurrencyConfig = {
        threshold: null,
        currency: "SEK",
        voucherCode: "NA",
      };
    } else {
      const resolvedCurrency = cart?.currencyCode;
      if (resolvedCurrency && typeof resolvedCurrency === "string") {
        const currencyKey = Object.keys(config).find(
          (k) => k.toUpperCase() === resolvedCurrency.toUpperCase(),
        );
        const currencyConfig = currencyKey ? config[currencyKey] : undefined;
        if (isFreeShippingCurrencyConfig(currencyConfig)) {
          const voucherCode =
            (typeof currencyConfig.voucherCode === "string"
              ? currencyConfig.voucherCode
              : undefined) ??
            (typeof config.voucherCode === "string"
              ? config.voucherCode
              : undefined);
          if (currencyConfig.threshold && voucherCode) {
            growthbookCurrencyConfig = {
              threshold: String(currencyConfig.threshold),
              currency: resolvedCurrency,
              voucherCode: String(voucherCode),
            };
          }
        }
      }
    }
  }

  const totals = cart?.totals;
  const cartTotal =
    totals?.grandTotal && totals.shipping !== undefined
      ? (totals.grandTotal.number ?? 0) - (totals.shipping.number ?? 0)
      : 0;
  const targetVoucherApplied = Boolean(
    growthbookCurrencyConfig &&
      cart?.vouchers?.some(
        (v) =>
          v.type === "code" && v.code === growthbookCurrencyConfig.voucherCode,
      ),
  );
  const isExpressShipping =
    cart?.shippingMethod?.code?.toUpperCase().includes("EXPRESS") ?? false;

  const threshold = growthbookCurrencyConfig?.threshold
    ? parseFloat(growthbookCurrencyConfig.threshold)
    : NaN;

  const shouldAutoApplyVoucher =
    Boolean(growthbookCurrencyConfig?.threshold) &&
    !isExpressShipping &&
    !Number.isNaN(threshold) &&
    cartTotal >= threshold &&
    !targetVoucherApplied;

  const shouldAutoRemoveVoucher =
    (isExpressShipping && targetVoucherApplied) ||
    (Boolean(growthbookCurrencyConfig?.threshold) &&
      targetVoucherApplied &&
      !Number.isNaN(threshold) &&
      cartTotal < threshold);

  const targetCode = growthbookCurrencyConfig?.voucherCode ?? null;
  const growthbookVoucherCodesToRemove =
    cart?.vouchers
      ?.filter(
        (v) =>
          v.type === "code" &&
          v.code !== targetCode &&
          v.name.includes("Free Shipping A/B test"),
      )
      .map((v) => (v.type === "code" ? v.code : ""))
      .filter(Boolean) ?? [];

  return {
    growthbookCurrencyConfig,
    shouldAutoApplyVoucher,
    shouldAutoRemoveVoucher,
    growthbookVoucherCodesToRemove,
  };
}

export function useMmFreeShippingVoucherSyncEffect() {
  const { cart, loading: cartLoading } = useCartState();
  const growthbook = useGreaseGrowthbook();

  const addVoucherExecute = useCartMethodExecute("addVoucher");
  const removeVoucherExecute = useCartMethodExecute("removeVoucher");
  const addVoucherRef = useRef(addVoucherExecute);
  const removeVoucherRef = useRef(removeVoucherExecute);
  const growthbookRef = useRef(growthbook);
  const cartRef = useRef(cart);
  addVoucherRef.current = addVoucherExecute;
  removeVoucherRef.current = removeVoucherExecute;
  growthbookRef.current = growthbook;
  cartRef.current = cart;
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (cartLoading || isProcessingRef.current) return;

    const {
      growthbookCurrencyConfig,
      shouldAutoApplyVoucher,
      shouldAutoRemoveVoucher,
      growthbookVoucherCodesToRemove,
    } = getVoucherSyncActions(
      cartRef.current,
      growthbookRef.current?.getFeatureValue(FREE_SHIPPING_FEATURE_KEY, null) ??
        null,
    );

    if (
      !shouldAutoApplyVoucher &&
      !shouldAutoRemoveVoucher &&
      growthbookVoucherCodesToRemove.length === 0
    ) {
      return;
    }

    const run = async () => {
      isProcessingRef.current = true;
      try {
        if (growthbookVoucherCodesToRemove.length > 0) {
          for (const code of growthbookVoucherCodesToRemove) {
            await removeVoucherRef.current({ voucherCode: code });
          }
        }

        if (!growthbookCurrencyConfig) return;

        if (shouldAutoApplyVoucher) {
          await addVoucherRef.current({
            voucherCode: growthbookCurrencyConfig.voucherCode,
          });
        } else if (shouldAutoRemoveVoucher) {
          await removeVoucherRef.current({
            voucherCode: growthbookCurrencyConfig.voucherCode,
          });
        }
      } catch (error) {
        console.error(
          "Failed to auto-apply/remove free shipping voucher:",
          error,
        );
      } finally {
        isProcessingRef.current = false;
      }
    };

    void run();
  }, [cart, cartLoading, growthbook]);
}

export const MmFreeShippingVoucherSync = () => {
  useMmFreeShippingVoucherSyncEffect();
  return null;
};
